import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { ChevronRight, BookOpen, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

/**
 * TeachingPhase
 * Renders phase.content.sections[]:
 *   { title, body, examples[], vocab[] }
 *
 * Backend sends resolved strings (recursive i18n), so fields are plain strings.
 * Fallback: title_tr/title_en, body_tr/body_en for legacy mock compat.
 */
function resolveStr(field, fieldTr, fieldEn, lang) {
  if (typeof field === 'string' && field) return field;
  return lang === 'tr'
    ? (fieldTr || fieldEn || '')
    : (fieldEn || fieldTr || '');
}

const sectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, delay: i * 0.08 },
  }),
};

export default function TeachingPhase({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'teaching-phase',
}) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();

  const sections = phase?.content?.sections || [];
  const maxXP = phase?.xp_in_phase || 0;

  // Section count label — interpolate {count} into the translation string.
  // The translation value itself carries "{count} bölüm" / "{count} sections",
  // so we must NOT prepend the number separately (that produced "1 {count} bölüm").
  const sectionsTpl = t('lesson.sections') || '{count} bölüm';
  const sectionsLabel = sectionsTpl.replace('{count}', sections.length);

  // Derive a header title from phase title
  const headerTitle =
    lang === 'tr'
      ? (phase?.title?.tr || phase?.title_tr || '')
      : (phase?.title?.en || phase?.title_en || phase?.title?.tr || '');

  const handleContinue = () => {
    onPhaseComplete({
      score: maxXP,
      maxScore: maxXP,
      answers: { viewed: true },
      timeSpent: 0,
    });
  };

  return (
    <motion.div
      initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5 w-full max-w-2xl mx-auto"
      data-testid={testId}
    >
      {/* ── Gradient header card ────────────────────────────────────────── */}
      <div
        className="rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 p-5 shadow-[0_4px_0_0_#3730a3] text-white flex items-center gap-3"
        aria-label={headerTitle || (t('lesson.teaching_header_label') || 'Ders içeriği')}
      >
        <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
          <BookOpen size={22} className="text-white" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-0.5 min-w-0">
          {headerTitle ? (
            <h2 className="text-2xl md:text-3xl font-black leading-snug" data-testid={`${testId}-header-title`}>
              {headerTitle}
            </h2>
          ) : (
            <h2 className="text-2xl md:text-3xl font-black leading-snug">
              {t('lesson.teaching_title') || 'Ders'}
            </h2>
          )}
          <p className="text-sm md:text-base text-white/75">
            {sections.length > 0
              ? sectionsLabel
              : t('lesson.read_and_continue') || 'Oku ve devam et'}
          </p>
        </div>
      </div>

      {/* ── Sections ────────────────────────────────────────────────────── */}
      {sections.length === 0 ? (
        <div className="text-center py-8 text-[var(--text-muted)] text-sm">
          {t('lesson.no_content') || 'İçerik bulunamadı.'}
        </div>
      ) : (
        sections.map((section, idx) => {
          const sectionTitle = resolveStr(
            section.title,
            section.title_tr,
            section.title_en,
            lang
          );
          const sectionBody = resolveStr(
            section.body,
            section.body_tr,
            section.body_en,
            lang
          );
          const examples = section.examples || [];
          const vocab = section.vocab || [];

          return (
            <motion.div
              key={idx}
              custom={idx}
              variants={shouldReduce ? {} : sectionVariants}
              initial="hidden"
              animate="visible"
              className="rounded-2xl border-2 border-gray-100 bg-white p-5 flex flex-col gap-4 shadow-[0_2px_0_0_#E5E5E5]"
              data-testid={`${testId}-section-${idx}`}
            >
              {/* Section header */}
              {sectionTitle && (
                <div className="flex items-center gap-2 pb-1 border-b border-gray-100">
                  <span
                    className="w-6 h-6 shrink-0 flex items-center justify-center bg-[var(--brand-primary)] text-white text-xs font-black rounded-lg"
                    aria-hidden="true"
                  >
                    {idx + 1}
                  </span>
                  <h3 className="text-base font-black text-[var(--text-main)] leading-snug">
                    {sectionTitle}
                  </h3>
                </div>
              )}

              {/* Body paragraph */}
              {sectionBody && (
                <p className="text-sm text-[var(--text-main)] leading-relaxed whitespace-pre-line">
                  {sectionBody}
                </p>
              )}

              {/* Examples */}
              {examples.length > 0 && (
                <ul
                  className="flex flex-col gap-2"
                  aria-label={t('teaching.examples') || 'Örnekler'}
                  data-testid={`${testId}-section-${idx}-examples`}
                >
                  {examples.map((ex, eIdx) => {
                    const exText =
                      typeof ex === 'string'
                        ? ex
                        : resolveStr(ex.text, ex.text_tr, ex.text_en, lang);
                    return (
                      <li
                        key={eIdx}
                        className="flex items-start gap-2 text-sm text-[var(--text-main)]"
                      >
                        <CheckCircle
                          size={16}
                          className="text-emerald-500 shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span>{exText}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Vocab chips */}
              {vocab.length > 0 && (
                <div
                  className="flex flex-wrap gap-2"
                  aria-label={t('teaching.vocab') || 'Kelimeler'}
                  data-testid={`${testId}-section-${idx}-vocab`}
                >
                  {vocab.map((v, vIdx) => {
                    const word =
                      typeof v === 'string'
                        ? v
                        : resolveStr(v.word, v.word_tr, v.word_en, lang);
                    const definition =
                      typeof v === 'string'
                        ? ''
                        : resolveStr(v.definition, v.definition_tr, v.definition_en, lang);

                    return (
                      <span
                        key={vIdx}
                        title={definition || undefined}
                        className="inline-flex items-center px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold rounded-lg cursor-default"
                        data-testid={`${testId}-vocab-${vIdx}`}
                      >
                        {word}
                      </span>
                    );
                  })}
                </div>
              )}
            </motion.div>
          );
        })
      )}

      {/* ── Continue CTA ────────────────────────────────────────────────── */}
      <div className="flex justify-center pt-2">
        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.97 }}
          onClick={handleContinue}
          className="flex items-center gap-2 px-10 py-4 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('exercise.continue') || 'Devam Et'}
          data-testid={`${testId}-continue`}
        >
          {t('exercise.continue') || 'Devam Et'}
          <ChevronRight size={22} aria-hidden="true" />
        </motion.button>
      </div>
    </motion.div>
  );
}
